/**
 * Confirm dialog for turning a manually-added blank box into a library placeholder. Shows a short summary
 * of the box plus two optional selectors (subcategory + destination); on confirm it creates the placeholder
 * (via {@link useCreatePlaceholderFromPart}) and hands the new id back so the drawer can bind it to the box.
 * A placeholder needs a name, so the confirm button stays disabled until the box has one.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ListFilter } from '@/api/generated/schemas';
import { Button, toast } from '@/shared/ui';
import { Dialog } from '@/shared/ui/dialog';
import {
  PlaceholderSubcategorySelector,
  type PlaceholderSubcategoryRef,
} from '@/modules/products/venua-planner/components/PlaceholderSubcategorySelector';
import { DestinationSelector } from '@/modules/products/venua-planner/components/DestinationSelector';
import type { DestinationRef } from '@/modules/products/venua-planner/venuaPlannerApi';
import { TYPE_COLOR, TypeIcon, type BoardPart } from '../model';
import { useCreatePlaceholderFromPart } from './useCreatePlaceholderFromPart';

/** Left-label width so both selectors line up. */
const LABEL_W = 120;

interface CreatePlaceholderFromPartDialogProps {
  part: BoardPart;
  onClose: () => void;
  /** Called with the id of the freshly created placeholder so the caller can bind it to the box. */
  onCreated: (placeholderId: string) => void;
}

export function CreatePlaceholderFromPartDialog({
  part,
  onClose,
  onCreated,
}: CreatePlaceholderFromPartDialogProps) {
  const { t } = useTranslation('venua-planner');
  const [subcategory, setSubcategory] = useState<PlaceholderSubcategoryRef | null>(null);
  const [destination, setDestination] = useState<DestinationRef | null>(null);
  const { createFromPart, isPending } = useCreatePlaceholderFromPart();

  const nameFilled = part.name.trim().length > 0;
  // Subcategories belong to a single placeholder type — scope the picker to the box's type.
  const subcategoryFilters: ListFilter[] = [{ field: 'type', ids: [part.type] }];

  const handleConfirm = async () => {
    if (!nameFilled || isPending) return;
    try {
      const newId = await createFromPart(part, { subcategory, destination });
      toast({ severity: 'success', description: t('costScheme.drawer.createDialog.created') });
      onCreated(newId);
      onClose();
    } catch {
      toast({ severity: 'error', description: t('costScheme.drawer.createDialog.createError') });
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('costScheme.drawer.createDialog.title')}
      icon="plus"
      width="md"
      // Overlay tier — same z-index as the drawer it opens from AND the selector popovers inside it. Stacking
      // then follows mount order (drawer → dialog → popover), so the dialog sits above the drawer while the
      // selector popover (mounted last) still floats above the dialog.
      zIndex="var(--phx-z-overlay)"
      closeLabel={t('costScheme.drawer.createDialog.cancel')}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('costScheme.drawer.createDialog.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!nameFilled || isPending}
            onClick={handleConfirm}
          >
            {t('costScheme.drawer.createDialog.confirm')}
          </Button>
        </>
      }
    >
      <div className="gap-phx-4 flex flex-col">
        <p className="text-phx-sm text-phx-text-2">{t('costScheme.drawer.createDialog.intro')}</p>

        <div className="rounded-phx-md border-phx-border bg-phx-surface-2 gap-phx-2 p-phx-3 flex items-center border">
          <span style={{ color: TYPE_COLOR[part.type] }} className="inline-flex flex-none">
            <TypeIcon type={part.type} transportKind={part.transportKind} size={14} />
          </span>
          <span className="text-phx-sm text-phx-text min-w-0 flex-1 truncate font-semibold">
            {part.name || t('costScheme.drawer.untitled')}
          </span>
          <span className="text-phx-xs text-phx-text-muted flex-none">
            {t(`costScheme.types.${part.type}`)}
          </span>
        </div>

        {nameFilled ? null : (
          <p className="text-phx-xs text-phx-warning-text">
            {t('costScheme.drawer.createDialog.nameRequired')}
          </p>
        )}

        <PlaceholderSubcategorySelector
          mode="single"
          layout="inline"
          labelWidth={LABEL_W}
          label={t('costScheme.drawer.createDialog.subcategory')}
          extraFilters={subcategoryFilters}
          selected={subcategory ? [subcategory] : []}
          onChange={(next) => setSubcategory(next[0] ?? null)}
        />
        <DestinationSelector
          mode="single"
          layout="inline"
          labelWidth={LABEL_W}
          label={t('costScheme.drawer.createDialog.destination')}
          selected={destination ? [destination] : []}
          onChange={(next) => setDestination(next[0] ?? null)}
        />
      </div>
    </Dialog>
  );
}
